// Pusat Regulasi (plan C1): the regulations the PBJ process data cites, for
// every signed-in role, read-only, and a second tab, Basis Pengetahuan (the
// files Pluto may cite, ./Knowledge.tsx), moved here from the Library.
//   rg_tabs       tabs             Regulasi | Basis Pengetahuan (?tab=pengetahuan)
//   rg_header     plain spacing    h1, count and the version of the data
//   rg_toolbar    divided section  search, untuk siapa (Non-Penyedia / Penyedia), jenis
//   rg_list       cards            one card per regulation: title with its status,
//                                  jenis and pengguna tags, how many steps cite it,
//                                  scope and use for a Penyedia as labelled fields,
//                                  the usage note, links to JDIH LKPP (new tab)
//   rg_authority  table            the authority matrix per actor and stage
//   rg_swakelola  table            the four Swakelola types
// Search and filters run here; the list is small and comes in one call.
import { useState } from "react";
import { navigate, useLocation } from "../router";
import { KnowledgeBrowser } from "./Knowledge";
import * as API from "../api";
import { formatCount } from "../lib/format";
import { Badge, EmptyState, Notice, PageHeader, SearchInput, SectionHead, SkeletonRows, Tabs, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

export const AUDIENCE_LABEL: Record<API.RegulationAudience, string> = {
  non_penyedia: "Non-Penyedia",
  penyedia: "Penyedia",
};

const KIND_LABEL: Record<string, string> = { peraturan: "Peraturan", panduan: "Panduan resmi" };

/** Lowercase text with accents folded, for a forgiving search. */
function fold(text: string): string {
  return text.toLowerCase().normalize("NFKD").replace(/\p{M}/gu, "");
}

export function filterRegulations(list: API.Regulation[], q: string, audience: string, kind: string): API.Regulation[] {
  const terms = fold(q).split(/\s+/).filter(Boolean);
  return list.filter((r) => {
    if (audience && !(r.audiences as string[]).includes(audience)) return false;
    if (kind && r.kind !== kind) return false;
    const hay = fold([r.title, r.scope, r.supplierUse ?? "", r.usage, r.publisher].join(" "));
    return terms.every((t) => hay.includes(t));
  });
}

export type RegulationsTab = "regulasi" | "pengetahuan";

const TABS = [
  { id: "regulasi", label: "Regulasi" },
  { id: "pengetahuan", label: "Basis Pengetahuan" },
];

export function RegulationsScreen() {
  useTitle("Regulasi");
  const location = useLocation();
  const tab: RegulationsTab = location.query.get("tab") === "pengetahuan" ? "pengetahuan" : "regulasi";
  const setTab = (next: string) => navigate(next === "pengetahuan" ? `${location.path}?tab=pengetahuan` : location.path, { replace: true });
  const center = useAsync(() => API.getRegulations(), []);
  const [q, setQ] = useState("");
  const [audience, setAudience] = useState("");
  const [kind, setKind] = useState("");

  const all = center.data?.regulations ?? [];
  const shown = filterRegulations(all, q, audience, kind);
  const authority = center.data?.authority ?? [];
  const swakelola = center.data?.swakelola ?? [];

  return (
    <>
      <PageHeader
        divided
        eyebrow="Regulasi / Rujukan"
        title="Pusat Regulasi"
        lead={
          center.data
            ? `${formatCount(all.length)} rujukan resmi yang dipakai alur PBJ dan jawaban Pluto. Selalu periksa naskah resmi, perubahan, dan ketentuan peralihannya.`
            : "Memuat daftar regulasi"
        }
      />

      <Tabs label="Isi Pusat Regulasi" idBase="rg" items={TABS} value={tab} onChange={setTab} />

      <div id="rg-panel" role="tabpanel" aria-labelledby={`rg-tab-${tab}`} className="rg-panel">
        {tab === "pengetahuan" ? (
          <KnowledgeBrowser />
        ) : (
          <>
            <section className="toolbar" aria-label="Cari regulasi">
              <div className="field-row" data-cols="3">
                <div className="field">
                  <label htmlFor="rg-q">Cari regulasi</label>
                  <SearchInput id="rg-q" value={q} onChange={setQ} placeholder="Nomor, judul, atau ruang lingkup" />
                </div>
                <div className="field">
                  <label htmlFor="rg-aud">Untuk</label>
                  <select id="rg-aud" value={audience} onChange={(e) => setAudience(e.currentTarget.value)}>
                    <option value="">Semua pengguna</option>
                    <option value="non_penyedia">Non-Penyedia</option>
                    <option value="penyedia">Penyedia</option>
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="rg-kind">Jenis</label>
                  <select id="rg-kind" value={kind} onChange={(e) => setKind(e.currentTarget.value)}>
                    <option value="">Semua jenis</option>
                    <option value="peraturan">Peraturan</option>
                    <option value="panduan">Panduan resmi</option>
                  </select>
                </div>
              </div>
            </section>

            {center.loading && !center.data ? (
              <SkeletonRows count={5} label="Memuat regulasi" />
            ) : center.error ? (
              <Notice tone="danger" title="Daftar regulasi belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={center.reload}>Muat ulang</button>}>
                <p>{API.errorMessage(center.error)}</p>
              </Notice>
            ) : all.length === 0 ? (
              <EmptyState icon="scale" title="Belum ada data regulasi" body="Data alur PBJ belum tersedia di server." />
            ) : (
              <div className="reg-groups">
                <section className="region" aria-labelledby="rg-list">
                  <SectionHead id="rg-list" title={`Regulasi rujukan (${formatCount(shown.length)})`} lead="Tautan membuka JDIH LKPP atau situs resmi di tab baru." />
                  {shown.length === 0 ? (
                    <EmptyState icon="filter" title="Tidak ada regulasi yang cocok" body="Ubah kata kunci atau pilih semua pengguna dan jenis." />
                  ) : (
                    <ul className="rows reg-list">
                      {shown.map((r) => (
                        <li key={r.id} className="row reg-row">
                          <div className="reg-head">
                            <span className="reg-icon" aria-hidden="true">
                              <Glyph name="scale" size={18} />
                            </span>
                            <div className="reg-head-text">
                              <span className="row-title">{r.title}</span>
                              <span className="reg-tags">
                                <Badge tone={r.verified ? "success" : "info"}>{r.verified ? `Tercatat di ${r.publisher}` : r.publisher}</Badge>
                                <span className="reg-tag">{KIND_LABEL[r.kind] ?? r.kind}</span>
                                <span className="reg-tag">{r.audiences.map((a) => AUDIENCE_LABEL[a]).join(", ")}</span>
                              </span>
                            </div>
                            {r.stepCount > 0 ? (
                              <span className="reg-stat" title={`Dirujuk ${formatCount(r.stepCount)} langkah di alur PBJ`}>
                                <strong className="num">{formatCount(r.stepCount)}</strong>
                                <span>langkah merujuk</span>
                              </span>
                            ) : null}
                          </div>
                          {r.scope || (r.supplierUse && r.supplierUse !== r.scope) ? (
                            <div className="reg-fields">
                              {r.scope ? (
                                <p className="reg-field">
                                  <span className="reg-field-label">
                                    Ruang lingkup<span className="visually-hidden">:</span>
                                  </span>{" "}
                                  {r.scope}
                                </p>
                              ) : null}
                              {r.supplierUse && r.supplierUse !== r.scope ? (
                                <p className="reg-field">
                                  <span className="reg-field-label">
                                    Bagi Penyedia<span className="visually-hidden">:</span>
                                  </span>{" "}
                                  {r.supplierUse}
                                </p>
                              ) : null}
                            </div>
                          ) : null}
                          {r.usage ? (
                            <p className="reg-usage">
                              <Glyph name="infoCircle" size={16} />
                              <span>{r.usage}</span>
                            </p>
                          ) : null}
                          {r.links.length > 0 ? (
                            <span className="reg-links">
                              {r.links.map((l) => (
                                <a key={l.url} className="btn btn-secondary btn-sm reg-link" href={l.url} target="_blank" rel="noopener noreferrer">
                                  <Glyph name="link" size={16} />
                                  <span>{l.label}</span>
                                </a>
                              ))}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {authority.length > 0 ? (
                  <section className="region" aria-labelledby="rg-authority">
                    <SectionHead id="rg-authority" title="Pembagian kewenangan" lead="Fungsi tiap pelaku per tahap beserta dasar hukumnya." />
                    <div className="records-table">
                      <table className="table">
                        <thead>
                          <tr>
                            <th scope="col">Pelaku</th>
                            <th scope="col">Tahap</th>
                            <th scope="col">Fungsi dan catatan</th>
                            <th scope="col">Dasar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {authority.map((a, i) => (
                            <tr key={`${a.actor}-${i}`}>
                              <td>{a.actor}</td>
                              <td>{a.stage}</td>
                              <td>
                                {a.duty}
                                {a.note ? <span className="cell-note">{a.note}</span> : null}
                              </td>
                              <td>{a.basis}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ) : null}

                {swakelola.length > 0 ? (
                  <section className="region" aria-labelledby="rg-swakelola">
                    <SectionHead id="rg-swakelola" title="Swakelola Tipe I sampai IV" lead="Pelaksana, penetapan tim, kontrak, dan serah terima tiap tipe." />
                    <div className="records-table">
                      <table className="table">
                        <thead>
                          <tr>
                            <th scope="col">Tipe</th>
                            <th scope="col">Pelaksana</th>
                            <th scope="col">Penetap Tim Pelaksana</th>
                            <th scope="col">Kontrak</th>
                            <th scope="col">Serah terima</th>
                          </tr>
                        </thead>
                        <tbody>
                          {swakelola.map((s) => (
                            <tr key={s.type}>
                              <td>Tipe {s.type}</td>
                              <td>{s.executor}</td>
                              <td>{s.executionTeam}</td>
                              <td>{s.contractSigner}</td>
                              <td>{s.handover}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
